import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'h1',
  'h2',
  'h3',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
  'hr',
  'pre',
  'code',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

/** Клиентская очистка перед показом и перед отправкой на API. */
export function sanitizeChatGptHtmlClient(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'title', 'colspan', 'rowspan'],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/iu,
  }).trim();
}

export function isEmptyRichHtml(html: string): boolean {
  const text = html
    .replace(/<br\s*\/?>/giu, '')
    .replace(/&nbsp;/giu, ' ')
    .replace(/<[^>]+>/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
  return text.length === 0;
}

/** Простой plain/markdown → HTML для вставки без text/html. */
export function plainOrMarkdownToHtml(plain: string): string {
  const normalized = plain.replace(/\r\n/gu, '\n').trimEnd();
  if (normalized.trim() === '') {
    return '<p></p>';
  }

  const blocks = normalized.split(/\n{2,}/u);
  const htmlParts: string[] = [];

  for (const block of blocks) {
    const lines = block.split('\n');
    const first = lines[0] ?? '';

    if (/^```/u.test(first)) {
      const code = lines.slice(1).join('\n').replace(/```$/u, '').trimEnd();
      htmlParts.push(`<pre><code>${escapeHtml(code)}</code></pre>`);
      continue;
    }

    if (/^#{1,3}\s+/u.test(first) && lines.length === 1) {
      const level = Math.min(3, (first.match(/^#+/u)?.[0].length ?? 1));
      const text = first.replace(/^#{1,3}\s+/u, '');
      htmlParts.push(`<h${level}>${escapeHtml(text)}</h${level}>`);
      continue;
    }

    if (/^>\s?/u.test(first)) {
      const quote = lines.map((line) => line.replace(/^>\s?/u, '')).join('<br>');
      htmlParts.push(`<blockquote><p>${escapeHtml(quote).replace(/&lt;br&gt;/gu, '<br>')}</p></blockquote>`);
      continue;
    }

    if (lines.every((line) => /^\s*[-*•]\s+/u.test(line))) {
      const items = lines
        .map((line) => `<li>${escapeHtml(line.replace(/^\s*[-*•]\s+/u, ''))}</li>`)
        .join('');
      htmlParts.push(`<ul>${items}</ul>`);
      continue;
    }

    if (lines.every((line) => /^\s*\d+[.)]\s+/u.test(line))) {
      const items = lines
        .map((line) => `<li>${escapeHtml(line.replace(/^\s*\d+[.)]\s+/u, ''))}</li>`)
        .join('');
      htmlParts.push(`<ol>${items}</ol>`);
      continue;
    }

    htmlParts.push(`<p>${escapeHtml(lines.join('\n')).replace(/\n/gu, '<br>')}</p>`);
  }

  return htmlParts.join('');
}

export function htmlToPlainTextClient(html: string): string {
  let text = html;
  text = text.replace(/<\/(p|h1|h2|h3|blockquote|pre|tr|table)>/giu, '\n');
  text = text.replace(/<br\s*\/?>/giu, '\n');
  text = text.replace(/<hr\s*\/?>/giu, '\n---\n');
  text = text.replace(/<\/li>/giu, '\n');
  text = text.replace(/<li[^>]*>/giu, '• ');
  text = text.replace(/<\/(ul|ol)>/giu, '\n');
  text = text.replace(/<a[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/giu, '$2 ($1)');
  text = text.replace(/<[^>]+>/gu, '');
  text = text
    .replace(/&nbsp;/giu, ' ')
    .replace(/&amp;/giu, '&')
    .replace(/&lt;/giu, '<')
    .replace(/&gt;/giu, '>')
    .replace(/&quot;/giu, '"')
    .replace(/&#39;/giu, "'");
  return text
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/u, ''))
    .join('\n')
    .replace(/\n{3,}/gu, '\n\n')
    .trim();
}

export async function copyHtmlAndText(html: string, plain: string): Promise<'html' | 'text'> {
  const safeHtml = sanitizeChatGptHtmlClient(html);
  const safePlain = plain.trim() !== '' ? plain : htmlToPlainTextClient(safeHtml);

  if (typeof navigator !== 'undefined' && navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([safeHtml], { type: 'text/html' }),
          'text/plain': new Blob([safePlain], { type: 'text/plain' }),
        }),
      ]);
      return 'html';
    } catch {
      // fallback below
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(safePlain);
    return 'text';
  }

  throw new Error('Буфер обмена недоступен');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
}
